import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Plus, Pencil, Trash2, Search, FileDown } from 'lucide-react';

const PACK_UNITS = [
  { v: 'kg', l: 'kg (kilogram)' },
  { v: 'g', l: 'g (gram)' },
  { v: 'L', l: 'L (litre)' },
  { v: 'ml', l: 'ml (millilitre)' },
  { v: 'pc', l: 'pc (piece)' },
];

const fmtINR = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function IngredientsTab() {
  const { getHeaders, API } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {id?, name, purchase_unit, purchase_price}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/recipes/ingredients`, { headers: getHeaders() });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !search.trim() || i.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const handleSave = async () => {
    if (!editing?.name || !editing?.purchase_unit || editing?.purchase_price === '' || editing?.purchase_price == null) {
      toast.error('Name, Purchase unit, and Price are required');
      return;
    }
    const price = Number(editing.purchase_price);
    const packSize = Number(editing.pack_size ?? 1);
    if (Number.isNaN(price) || price < 0) return toast.error('Price must be a valid non-negative number');
    if (!(packSize > 0)) return toast.error('Pack size must be greater than 0');
    const payload = {
      name: editing.name.trim(),
      purchase_unit: editing.purchase_unit.trim(),
      purchase_price: price,
      pack_size: packSize,
      pack_unit: editing.pack_unit || 'kg',
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/recipes/ingredients/${editing.id}`, payload, { headers: getHeaders() });
        toast.success('Ingredient updated');
      } else {
        await axios.post(`${API}/recipes/ingredients`, payload, { headers: getHeaders() });
        toast.success('Ingredient added');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ingredient "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/recipes/ingredients/${row.id}`, { headers: getHeaders() });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/recipes/ingredients/upload`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported: ${data.created} new · ${data.updated} updated · ${data.skipped} skipped`);
      if (data.errors?.length) {
        console.warn('[Ingredient upload errors]', data.errors);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/recipes/ingredients/template`, {
        headers: getHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ingredients_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  return (
    <div className="space-y-4" data-testid="ingredients-tab">
      {/* Toolbar */}
      <Card className="border-[#E2E8DF]">
        <CardContent className="py-3 px-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-[#5C6B64] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search ingredients…"
              data-testid="ingredients-search"
              className="h-9 pl-8 text-sm" />
          </div>
          <span className="text-[11px] text-[#5C6B64]" data-testid="ingredients-count">
            {filtered.length} item{filtered.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={downloadTemplate}
              data-testid="ingredients-template-btn" className="h-9">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> Template
            </Button>
            <label className="inline-flex">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={handleFile}
                disabled={uploading} className="hidden" data-testid="ingredients-upload-input" />
              <Button asChild variant="outline" size="sm" disabled={uploading} className="h-9"
                data-testid="ingredients-upload-btn">
                <span>
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {uploading ? 'Uploading…' : 'Upload Excel'}
                </span>
              </Button>
            </label>
            <Button size="sm"
              className="h-9 bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white"
              onClick={() => setEditing({ name: '', purchase_unit: '', purchase_price: '', pack_size: 1, pack_unit: 'kg' })}
              data-testid="ingredients-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add ingredient
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="px-0 pb-0 pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-[#5C6B64]">No ingredients yet</p>
              <p className="text-xs text-[#5C6B64] mt-1">Upload an Excel file or add ingredients manually to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-y border-[#E2E8DF]">
                    <th className="px-4 py-2 text-left">Item name</th>
                    <th className="px-4 py-2 text-left">Purchase unit</th>
                    <th className="px-4 py-2 text-right">Pack size</th>
                    <th className="px-4 py-2 text-right">Purchase price</th>
                    <th className="px-4 py-2 text-right">Effective ₹ / unit</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(it => (
                    <tr key={it.id} className="border-b border-[#E2E8DF]/60 hover:bg-[#F9F9F8]"
                      data-testid={`ingredient-row-${it.id}`}>
                      <td className="px-4 py-2 font-medium text-[#1A2421]">{it.name}</td>
                      <td className="px-4 py-2 text-[#5C6B64]">{it.purchase_unit}</td>
                      <td className="px-4 py-2 text-right text-xs tabular-nums text-[#5C6B64]">
                        {it.pack_size || 1} {it.pack_unit || 'kg'}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-[#1A2421] tabular-nums">
                        {fmtINR(it.purchase_price)}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-[color:var(--color-primary)] tabular-nums">
                        {fmtINR(it.unit_price)}<span className="text-[10px] text-[#5C6B64]">/{it.pack_unit || 'kg'}</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditing({ ...it })}
                            data-testid={`ingredient-edit-${it.id}`}
                            className="w-7 h-7 rounded-md text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)]/10 flex items-center justify-center">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(it)}
                            data-testid={`ingredient-delete-${it.id}`}
                            className="w-7 h-7 rounded-md text-[#C45A41] hover:bg-[#C45A41]/10 flex items-center justify-center">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="ingredient-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit ingredient' : 'Add ingredient'}</DialogTitle>
            <DialogDescription className="text-xs">
              Pack size + Pack unit drives accurate costing (e.g., 15 L tin @ ₹2326 → ₹155/L effective).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Item name</Label>
              <Input value={editing?.name || ''}
                onChange={e => setEditing(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Cooking Oil" maxLength={150}
                data-testid="ing-name-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Purchase unit (display)</Label>
              <Input value={editing?.purchase_unit || ''}
                onChange={e => setEditing(f => ({ ...f, purchase_unit: e.target.value }))}
                placeholder="e.g., 15 L Tin / 1 Kg block / Tray of 30" maxLength={60}
                data-testid="ing-unit-input" className="mt-1" />
              <p className="text-[10px] text-[#5C6B64] mt-1">Shown in the recipe picker. Pack size + Pack unit below drive the costing math.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-[#1A2421]">Pack size</Label>
                <Input type="number" inputMode="decimal" step="any" min="0.001"
                  value={editing?.pack_size ?? 1}
                  onChange={e => setEditing(f => ({ ...f, pack_size: e.target.value }))}
                  placeholder="15"
                  data-testid="ing-pack-size-input" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1A2421]">Pack unit</Label>
                <Select value={editing?.pack_unit || 'kg'}
                  onValueChange={v => setEditing(f => ({ ...f, pack_unit: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="ing-pack-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PACK_UNITS.map(u => (
                      <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Purchase price (₹ per pack)</Label>
              <Input type="number" inputMode="decimal" step="any" min="0"
                value={editing?.purchase_price ?? ''}
                onChange={e => setEditing(f => ({ ...f, purchase_price: e.target.value }))}
                placeholder="e.g., 2326 (for a 15 L tin)"
                data-testid="ing-price-input" className="mt-1" />
            </div>
            {/* Live effective price preview */}
            {(() => {
              const price = Number(editing?.purchase_price);
              const size = Number(editing?.pack_size);
              if (!(price > 0 && size > 0)) return null;
              const u = editing?.pack_unit || 'kg';
              return (
                <div className="p-2.5 rounded-md bg-[color:var(--color-primary)]/[0.05] border border-[color:var(--color-primary)]/15 text-[11px]"
                  data-testid="ing-effective-preview">
                  <span className="text-[#5C6B64]">Effective price: </span>
                  <span className="font-bold text-[color:var(--color-primary)] tabular-nums">
                    {fmtINR(price / size)} / {u}
                  </span>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="ing-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} data-testid="ing-save-btn"
              className="bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white">
              {editing?.id ? 'Save changes' : 'Add ingredient'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
